import { create } from 'zustand';
import type { Band } from '../models/types';
import { ApiService } from '../services/api';
import { StorageService } from '../services/storage';


interface BandState {
  bands: Band[];
  searchVal: string;
  showSearch: boolean;
  isRefreshing: boolean;
  selectedBands: Set<string>; // Use band identifiers instead of objects
  featuredBands: Set<string>; // Use band identifiers instead of objects
  selectedBandYears: Map<string, Set<string>>;
  selectedBandMonthYears: Map<string, Set<string>>;
  isViewingGreat78Artists: boolean;

  // Actions
  fetchBands: () => Promise<void>;
  searchValUpdate: (val: string) => void;
  toggleSearch: () => void;
  toggleBandSelection: (band: Band) => void;
  toggleYearSelection: (band: Band, year: string) => void;
  toggleMonthYearSelection: (band: Band, monthYear: string) => void;
  toggleBandFeatured: (band: Band) => void;
  isBandSelected: (band: Band) => boolean;
  isYearSelected: (band: Band, year: string) => boolean;
  isMonthYearSelected: (band: Band, monthYear: string) => boolean;
  isBandFeatured: (band: Band) => boolean;
  isBandCustomized: (band: Band) => boolean;
  isBandYearCustomized: (band: Band, year: string) => boolean;
  getSelectedBandObjects: () => Band[];
  getFeaturedBandObjects: () => Band[];
  getActiveBands: () => (Band & { selectedYears: string[]; selectedYearMonths: string[] })[];
  getRandomValue: <T>(arr: T[]) => T;
  saveFavoriteArtists: () => Promise<boolean>;
  loadFavoriteArtists: () => Promise<boolean>;
  saveFeaturedBands: () => Promise<boolean>;
  loadFeaturedBands: () => Promise<boolean>;
  randomizeArtists: () => Promise<boolean>;
  selectAllBands: () => boolean;
  clearSelectedBands: () => void;
  setSelectedBandsAsActive: () => boolean;
  setActiveBandFromShare: (collection: string, source: string) => void;
}

const sourceValue = (source: string): number => (source === 'ls' ? 0 : 1);

export const useBandStore = create<BandState>((set, get) => ({
  bands: [],
  searchVal: '',
  showSearch: false,
  isRefreshing: false,
  selectedBands: new Set(),
  featuredBands: new Set(),
  selectedBandYears: new Map(),
  selectedBandMonthYears: new Map(),
  isViewingGreat78Artists: false,

  

  fetchBands: async () => {
    try {
      const fetchedBands = await ApiService.fetchBands();
      // Strip leading '!' from order field (matches Flutter behavior)
      for (const band of fetchedBands) {
        band.order = band.order.replace(/^!/, '');
      }
      const sortedBands = fetchedBands.sort((a, b) => {
        const sourceComparison = sourceValue(a.source) - sourceValue(b.source);
        if (sourceComparison !== 0) return sourceComparison;
        return a.order.toLowerCase().localeCompare(b.order.toLowerCase());
      });

      set({ bands: sortedBands });
      console.log(`Fetched ${sortedBands.length} bands`);
    } catch (error) {
      console.error('Error fetching bands:', error);
    }
  },

  searchValUpdate: (val: string) => set({ searchVal: val }),

  toggleSearch: () => set((state) => ({ showSearch: !state.showSearch })),

  toggleBandSelection: (band: Band) => {
    const { selectedBands, selectedBandYears, selectedBandMonthYears } = get();
    const newSelectedBands = new Set(selectedBands);
    const bandIdentifier = `${band.collection}|${band.source}`;

    if (newSelectedBands.has(bandIdentifier)) {
      newSelectedBands.delete(bandIdentifier);
      // Only create new Maps if we're actually changing them
      const hasYears = selectedBandYears.has(band.bandName);
      const hasMonthYears = selectedBandMonthYears.has(band.bandName);

      if (hasYears || hasMonthYears) {
        const newYears = new Map(selectedBandYears);
        const newMonthYears = new Map(selectedBandMonthYears);
        newYears.delete(band.bandName);
        newMonthYears.delete(band.bandName);
        set({
          selectedBands: newSelectedBands,
          selectedBandYears: newYears,
          selectedBandMonthYears: newMonthYears,
        });
      } else {
        // Maps didn't change, don't create new instances
        set({ selectedBands: newSelectedBands });
      }
    } else {
      newSelectedBands.add(bandIdentifier);
      // Maps didn't change, don't create new instances
      set({ selectedBands: newSelectedBands });
    }
    // Auto-save after selection change
    get().saveFavoriteArtists();
  },

  toggleYearSelection: (band: Band, year: string) => {
    const { selectedBandYears, selectedBandMonthYears } = get();
    const newSelectedBandYears = new Map(selectedBandYears);
    const newSelectedBandMonthYears = new Map(selectedBandMonthYears);

    const bandYears = newSelectedBandYears.get(band.bandName) || new Set();

    if (bandYears.has(year)) {
      bandYears.delete(year);
      // Clear related month-years when a year is deselected
      const bandMonthYears = newSelectedBandMonthYears.get(band.bandName);
      if (bandMonthYears) {
        for (const monthYear of bandMonthYears) {
          if (monthYear.startsWith(year)) {
            bandMonthYears.delete(monthYear);
          }
        }
      }
    } else {
      bandYears.add(year);
    }

    if (bandYears.size === 0) {
      newSelectedBandYears.delete(band.bandName);
    } else {
      newSelectedBandYears.set(band.bandName, bandYears);
    }

    set({
      selectedBandYears: newSelectedBandYears,
      selectedBandMonthYears: newSelectedBandMonthYears,
    });
    // Auto-save after year selection change
    get().saveFavoriteArtists();
  },

  toggleMonthYearSelection: (band: Band, monthYear: string) => {
    const { selectedBandMonthYears, selectedBandYears } = get();
    const newSelectedBandMonthYears = new Map(selectedBandMonthYears);
    const newSelectedBandYears = new Map(selectedBandYears);

    const bandMonthYears = newSelectedBandMonthYears.get(band.bandName) || new Set();

    if (bandMonthYears.has(monthYear)) {
      bandMonthYears.delete(monthYear);
    } else {
      bandMonthYears.add(monthYear);
      // Ensure the year is selected when a month-year is selected
      const year = monthYear.split('-')[0];
      const bandYears = newSelectedBandYears.get(band.bandName) || new Set();
      bandYears.add(year);
      newSelectedBandYears.set(band.bandName, bandYears);
    }

    if (bandMonthYears.size === 0) {
      newSelectedBandMonthYears.delete(band.bandName);
    } else {
      newSelectedBandMonthYears.set(band.bandName, bandMonthYears);
    }

    set({
      selectedBandYears: newSelectedBandYears,
      selectedBandMonthYears: newSelectedBandMonthYears,
    });
    // Auto-save after month-year selection change
    get().saveFavoriteArtists();
  },

  toggleBandFeatured: (band: Band) => {
    const { featuredBands } = get();
    const newFeaturedBands = new Set(featuredBands);
    const bandIdentifier = `${band.collection}|${band.source}`;

    if (newFeaturedBands.has(bandIdentifier)) {
      newFeaturedBands.delete(bandIdentifier);
    } else {
      newFeaturedBands.add(bandIdentifier);
    }

    set({ featuredBands: newFeaturedBands });
    get().saveFeaturedBands();
  },

  isBandSelected: (band: Band) => get().selectedBands.has(`${band.collection}|${band.source}`),

  isYearSelected: (band: Band, year: string) =>
    (get().selectedBandYears.get(band.bandName) || new Set()).has(year),

  isMonthYearSelected: (band: Band, monthYear: string) =>
    (get().selectedBandMonthYears.get(band.bandName) || new Set()).has(monthYear),

  isBandFeatured: (band: Band) => get().featuredBands.has(`${band.collection}|${band.source}`),

  // Helper methods to get actual band objects
  getSelectedBandObjects: () => {
    const { selectedBands, bands } = get();
    return bands.filter(band => selectedBands.has(`${band.collection}|${band.source}`));
  },

  getFeaturedBandObjects: () => {
    const { featuredBands, bands } = get();
    return bands.filter(band => featuredBands.has(`${band.collection}|${band.source}`));
  },

  isBandCustomized: (band: Band) =>
    (get().selectedBandYears.get(band.bandName)?.size || 0) > 0,

  isBandYearCustomized: (band: Band, year: string) =>
    (get().selectedBandMonthYears.get(band.bandName) || new Set())
      .has(year) ||
    Array.from(get().selectedBandMonthYears.get(band.bandName) || [])
      .some(monthYear => monthYear.includes(year)),

  // Build active bands with resolved customizations (similar to Flutter's activeBands concept)
  getActiveBands: () => {
    const { selectedBands, selectedBandYears, selectedBandMonthYears, bands } = get();

    return bands.filter(band => selectedBands.has(`${band.collection}|${band.source}`)).map(band => {
      const bandYears = selectedBandYears.get(band.bandName) || new Set<string>();
      const bandMonthYears = selectedBandMonthYears.get(band.bandName) || new Set<string>();

      // Clone and process: strip years that have month-year sub-selections
      const processedYears = new Set(bandYears);
      for (const monthYear of bandMonthYears) {
        const yearFromMonthYear = monthYear.split('-')[0];
        processedYears.delete(yearFromMonthYear);
      }

      return {
        ...band,
        selectedYears: Array.from(processedYears),
        selectedYearMonths: Array.from(bandMonthYears),
      };
    });
  },

  // Helper to get random value from array
  getRandomValue: <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)],

  saveFavoriteArtists: async () => {
    try {
      const { selectedBands, selectedBandYears, selectedBandMonthYears, getSelectedBandObjects } = get();

      if (selectedBands.size === 0) return false;

      const selectedBandObjects = getSelectedBandObjects();
      let saveBandNames = '';
      for (const band of selectedBandObjects) {
        saveBandNames += `${band.bandName}\n`;

        // Save customized year selections
        const years = selectedBandYears.get(band.bandName);
        if (years) {
          for (const year of years) {
            saveBandNames += `year:${year}\n`;
          }
        }

        // Save customized month selections
        const monthYears = selectedBandMonthYears.get(band.bandName);
        if (monthYears) {
          for (const monthYear of monthYears) {
            saveBandNames += `yearmonth:${monthYear}\n`;
          }
        }

        saveBandNames += '---\n';
      }

      await StorageService.clearPreviousSession();
      await StorageService.setPreviousSession(saveBandNames);
      await get().saveFeaturedBands();

      return true;
    } catch (error) {
      console.error('Error saving favorite artists:', error);
      return false;
    }
  },

  loadFavoriteArtists: async () => {
    try {
      const savedData = await StorageService.getPreviousSession();
      if (!savedData) return false;

      const lines = savedData.split('\n');
      const newSelectedBands = new Set<string>();
      const newSelectedBandYears = new Map<string, Set<string>>();
      const newSelectedBandMonthYears = new Map<string, Set<string>>();

      let currentBandName: string | null = null;
      let years = new Set<string>();
      let monthYears = new Set<string>();

      for (const line of lines) {
        if (line === '') continue;
        if (line === '---') {
          currentBandName = null;
          years = new Set();
          monthYears = new Set();
          continue;
        }

        if (!line.startsWith('year:') && !line.startsWith('yearmonth:')) {
          // Band name - find the band to get the identifier
          const band = get().bands.find(b => b.bandName === line);
          if (band) {
            const bandIdentifier = `${band.collection}|${band.source}`;
            newSelectedBands.add(bandIdentifier);
            currentBandName = band.bandName;
          }
        } else if (line.startsWith('year:') && currentBandName) {
          const year = line.substring(5);
          years.add(year);
          newSelectedBandYears.set(currentBandName, years);
        } else if (line.startsWith('yearmonth:') && currentBandName) {
          const monthYear = line.substring(10);
          monthYears.add(monthYear);
          newSelectedBandMonthYears.set(currentBandName, monthYears);
        }
      }

      set({
        selectedBands: newSelectedBands,
        selectedBandYears: newSelectedBandYears,
        selectedBandMonthYears: newSelectedBandMonthYears,
      });

      return true;
    } catch (error) {
      console.error('Error loading favorite artists:', error);
      return false;
    }
  },

  saveFeaturedBands: async () => {
    try {
      const { featuredBands } = get();
      let featuredData = '';

      for (const bandIdentifier of featuredBands) {
        featuredData += `${bandIdentifier}\n`;
      }

      await StorageService.clearFeatured();
      await StorageService.setFeatured(featuredData);
      console.log(`Saved ${featuredBands.size} featured bands`);

      return true;
    } catch (error) {
      console.error('Error saving featured bands:', error);
      return false;
    }
  },

  loadFeaturedBands: async () => {
    try {
      const savedData = await StorageService.getFeatured();
      if (!savedData) return false;

      const lines = savedData.split('\n');
      const newFeaturedBands = new Set<string>();

      for (const line of lines) {
        if (!line) continue;
        newFeaturedBands.add(line);
      }

      set({ featuredBands: newFeaturedBands });
      console.log(`Loaded ${newFeaturedBands.size} featured bands`);

      return true;
    } catch (error) {
      console.error('Error loading featured bands:', error);
      return false;
    }
  },

  randomizeArtists: async () => {
    try {
      const { bands } = get();

      // Clear current selections
      set({
        selectedBands: new Set(),
        selectedBandYears: new Map(),
        selectedBandMonthYears: new Map(),
      });

      // Select 3-7 random Live Shuffle bands
      const shuffleBands = bands.filter(band => band.source === 'ls');
      const numBandsToSelect = 3 + Math.floor(Math.random() * 5);
      const shuffled = [...shuffleBands].sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, numBandsToSelect);

      const selectedIdentifiers = selected.map(band => `${band.collection}|${band.source}`);
      set({ selectedBands: new Set(selectedIdentifiers) });
      return true;
    } catch (error) {
      console.error('Error randomizing artists:', error);
      return false;
    }
  },

  selectAllBands: () => {
    try {
      const { bands } = get();
      const shuffleBands = bands.filter(band => band.source === 'ls');

      const selectedIdentifiers = shuffleBands.map(band => `${band.collection}|${band.source}`);
      set({
        selectedBands: new Set(selectedIdentifiers),
        selectedBandYears: new Map(),
        selectedBandMonthYears: new Map(),
      });

      return true;
    } catch (error) {
      console.error('Error selecting all bands:', error);
      return false;
    }
  },

  clearSelectedBands: () => {
    set({
      selectedBands: new Set(),
      selectedBandYears: new Map(),
      selectedBandMonthYears: new Map(),
    });
  },

  setSelectedBandsAsActive: () => {
    const { selectedBands } = get();
    if (selectedBands.size === 0) return false;

    // TODO: Implement setting active bands and fetching initial songs
    console.log('Setting selected bands as active:', selectedBands.size);
    return true;
  },

  setActiveBandFromShare: (collection: string, source: string) => {
    const { bands, selectedBands } = get();
    const bandIdentifier = `${collection}|${source}`;

    if (!selectedBands.has(bandIdentifier)) {
      const band = bands.find(b => b.collection === collection && b.source === source);
      if (band) {
        const newSelectedBands = new Set(selectedBands);
        newSelectedBands.add(bandIdentifier);
        set({ selectedBands: newSelectedBands });
        get().saveFavoriteArtists();
      }
    }
  },
}));